import { Injectable } from '@angular/core';

/**
 * Forma mínima de una cuota que entiende el servicio. Los componentes de edición
 * de planes tienen FormGroups con campos distintos (`id`/`isLocked` vs
 * `installmentId` vs `generateLink`), pero la matemática de refinanciación solo
 * mira estos cuatro. Pasá `control.getRawValue()` o un objeto plano.
 */
export interface InstallmentLike {
  paymentStatus?: string | null;
  amountToPay?: string | number | null;
  amountPaid?: string | number | null;
  installmentNumber?: number | null;
}

/**
 * 🆕 Lógica compartida de refinanciación / redistribución de planes de pago.
 *
 * Extraída de los 3 componentes de edición (edit-payment-plans,
 * edit-corporate-participant-plans, edit-corporate-company-plans) que duplicaban
 * la misma matemática a mano — fuente del bug de double-count del shortfall de
 * cuotas parciales 'keep'. Centralizar acá evita que las 3 copias diverjan.
 *
 * Es STATELESS y puro: opera sobre números/arrays planos, nunca sobre FormArrays
 * ni sobre `this` de un componente. Eso lo hace trivialmente testeable.
 */
@Injectable({ providedIn: 'root' })
export class PaymentPlanRefinanceService {
  /**
   * 💰 Convierte un string de moneda formateada (es-CO) a número decimal.
   * "$1.234,56" → 1234.56. Un number pasa derecho. Basura → 0.
   */
  parseFormattedCurrency(value: string | number | null | undefined): number {
    if (typeof value === 'number') {
      return value;
    }
    if (!value || typeof value !== 'string') {
      return 0;
    }
    const numericString = value
      .replace(/[$\s]/g, '')   // símbolo de moneda y espacios
      .replace(/\./g, '')      // separador de miles
      .replace(/,/g, '.');     // coma decimal → punto
    return parseFloat(numericString) || 0;
  }

  /** Redondeo a 2 decimales estable (evita el error de coma flotante). */
  roundToTwo(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  /** true si total y remaining coinciden dentro de la tolerancia (default 1 centavo). */
  isBalanced(total: number, remaining: number, eps = 0.01): boolean {
    return Math.abs(this.roundToTwo(total) - this.roundToTwo(remaining)) < eps;
  }

  /**
   * Saldo pendiente de UNA cuota: max(0, amountToPay - amountPaid), redondeado.
   * Acepta valores formateados o numéricos.
   */
  shortfall(row: InstallmentLike): number {
    const toPay = this.parseFormattedCurrency(row?.amountToPay ?? 0);
    const paid = this.parseFormattedCurrency(row?.amountPaid ?? 0);
    return Math.max(0, this.roundToTwo(toPay - paid));
  }

  /**
   * Suma del shortfall de las cuotas PARTIALLY_PAID mantenidas ('keep').
   *
   * Estas cuotas siguen en el plan y ya aportan su shortfall al total
   * (vía {@link totalAssignedPending}); `remainingBalance` también lo incluye.
   * Por eso NO debe repartirse de nuevo entre las cuotas nuevas al refinanciar:
   * hacerlo lo contaría dos veces y descuadraría el total (bug de double-count).
   */
  preservedPartialShortfall(rows: InstallmentLike[]): number {
    return (rows ?? []).reduce((sum, row) => {
      if (row?.paymentStatus !== 'PARTIALLY_PAID') return sum;
      return sum + this.shortfall(row);
    }, 0);
  }

  /**
   * Total asignado = Σ max(0, amountToPay - amountPaid) sobre TODAS las cuotas,
   * redondeado. Es el monto que la grilla le "roba" al saldo restante; se compara
   * contra `remainingBalance` para habilitar Guardar.
   */
  totalAssignedPending(rows: InstallmentLike[]): number {
    const sum = (rows ?? []).reduce((acc, row) => {
      const toPay = this.parseFormattedCurrency(row?.amountToPay ?? 0);
      const paid = this.parseFormattedCurrency(row?.amountPaid ?? 0);
      return acc + Math.max(0, toPay - paid);
    }, 0);
    return this.roundToTwo(sum);
  }

  /**
   * Monto a repartir entre las cuotas NUEVAS al refinanciar:
   *   remainingBalance − preservedPartialShortfall(rows)
   *
   * El fix del double-count vive acá: `remainingBalance` ya incluye el shortfall
   * de las parciales 'keep', así que repartir `remainingBalance` completo lo
   * contaría dos veces. Restamos el shortfall preservado primero.
   */
  distributableAmount(remainingBalance: number, rows: InstallmentLike[]): number {
    return this.roundToTwo(remainingBalance - this.preservedPartialShortfall(rows));
  }

  /**
   * Reparte `total` en `count` cuotas con prorrateo igualitario. La última
   * absorbe la diferencia por redondeo, así que la suma del array es exactamente
   * roundToTwo(total). Devuelve [] si count <= 0.
   *
   * Unifica los 3 bucles duplicados (applyRefinancing, redistributeShortfall,
   * recalculateFromInstallment).
   */
  splitEvenly(total: number, count: number): number[] {
    if (count <= 0) return [];
    const per = this.roundToTwo(total / count);
    const out: number[] = [];
    let accumulated = 0;
    for (let i = 0; i < count; i++) {
      const isLast = i === count - 1;
      if (isLast) {
        out.push(this.roundToTwo(total - accumulated));
      } else {
        out.push(per);
        accumulated = this.roundToTwo(accumulated + per);
      }
    }
    return out;
  }

  /**
   * Primer número de cuota libre = max(todos los usados) + 1, o 1 si no hay.
   * Considera tanto las cuotas del FormArray como las PAID del snapshot (que
   * viven fuera pero tienen su número reservado). La numeración nunca decrece ni
   * pisa una cuota fija.
   */
  firstFreeInstallmentNumber(
    formNumbers: Array<number | null | undefined>,
    paidNumbers: Array<number | null | undefined> = []
  ): number {
    const all = [...(formNumbers ?? []), ...(paidNumbers ?? [])].map(n => Number(n || 0));
    return all.length ? Math.max(...all) + 1 : 1;
  }

  /**
   * Vista previa de sobrepago: cuenta las cuotas cuyo pagado supera el monto a pagar
   * por más de 0.01 y suma esas diferencias. El backend las convierte en excedentes.
   */
  overpaymentPreview(
    rows: Array<{ amountToPay: number; amountPaid: number }>
  ): { count: number; total: number } {
    let count = 0;
    let total = 0;
    for (const r of rows ?? []) {
      const diff = Number(r?.amountPaid || 0) - Number(r?.amountToPay || 0);
      if (diff > 0.01) {
        count++;
        total += diff;
      }
    }
    return { count, total: this.roundToTwo(total) };
  }
}
